import React from 'react';
import PropTypes from 'prop-types';
import {QuestionFooter} from '../SharedComponents/QuestionFooter';
import {QUESTION_CONTENTS} from '@learningblocks/exp-constants/lib/Utilities';
import {Question} from '../SharedComponents/Question';
import {SliderSelectOption} from '../SharedComponents/SliderSelectOption';
import {FooterFeedback} from '../SharedComponents/FooterFeedback';
import '../SharedComponents/CommonStyles';

/**
 * Experimental-Design Question component [Slider-Select]:
 *  - You will have n number of Character-Sequence patterns
 *  - You will have to highlight the correct part of sequence
 *  - You can drag the highligt on character-sequence to correct position and submit
 */
export function SliderSelect(props) {
  const isFeedbackCorrect = () => {
    const isCorrect = props.sliders.every(
      (slider) =>
        slider.current.start === slider.answer.start &&
        slider.current.end === slider.answer.end
    );
    return isCorrect ? true : false;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else {
      let updated = props.sliders.some((slider) => slider.updated);
      if (updated) return false;
      else return true;
    }
  };

  const renderSliders = (key) => {
    return props.sliders.map((slider, id) => (
      <SliderSelectOption
        key={`${key}_${id}`}
        {...slider}
        updateSelection={props.updateSelection}
        componentID={props.id}
      />
    ));
  };

  const renderQuestion = (key) => (
    <Question
      key={key}
      className={`${props.className}__text`}
      question={props.question}
    />
  );

  const renderByType = (type, key) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key);
      case QUESTION_CONTENTS.SLIDERS:
        return renderSliders(key);
      default:
        return null;
    }
  };

  const renderContents = () => props.content.map(renderByType);

  return (
    <div className={props.className}>
      {renderContents()}
      <QuestionFooter
        className={`${props.className}__footer`}
        complete={props.complete}
        componentID={props.id}
        componentType={props.type}
        totalAttempts={props.totalAttempts}
        submitAnswer={props.submitAnswer}
        submitDisabled={setSubmitDisabled()}>
        <FooterFeedback
          showFeedback={props.showFeedback}
          isCorrect={isFeedbackCorrect()}
        />
      </QuestionFooter>
    </div>
  );
}

SliderSelect.propTypes = {
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.oneOfType([
    PropTypes.oneOf(['question_color']),
    PropTypes.string,
  ]).isRequired,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,

  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(['DNDQ_COLOR']),
  /**
   * list of sliders that you wanna display on your question
   */
  sliders: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Unique number Id for individual slider
       *
       */
      id: PropTypes.number,
      /**
       * String sequence over which we do selection
       */
      sequence: PropTypes.string,
      /**
       * Answer object containing start/end index in sequence and answer substring
       */
      answer: PropTypes.shape({
        /**
         * Answer substring
         */
        text: PropTypes.string,
        /**
         * Start index of answer substring
         */
        start: PropTypes.number,
        /**
         * End index of answer substring
         */
        end: PropTypes.number,
      }),
      current: PropTypes.shape({
        /**
         * current selected substring
         */
        text: PropTypes.string,
        /**
         * Start index of current selected substring
         */
        start: PropTypes.number,
        /**
         * End index of current selected substring
         */
        end: PropTypes.number,
      }),
      /**
       * Color to be set for the highlighted string
       */
      highlightClass: PropTypes.oneOf(['red', 'green', 'blue', 'null']),
      /**
       * Boolean value specifying the slider is disabled
       */
      disable: PropTypes.bool,
    })
  ).isRequired,
  /**
   * Boolean value specifying if we should show feedback
   */
  showFeedback: PropTypes.bool.isRequired,
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * Callback function to be executed when selection sequence updated
   */
  updateSelection: PropTypes.func.isRequired,
};

SliderSelect.defaultProps = {
  className: 'question_color',
  complete: false,
  content: [],
  question: '',
  totalAttempts: 0,
  type: 'DNDQ_COLOR',
  sliders: [],
  showFeedback: false,
};
